import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Sparkles, BookOpen, GitBranch, Users } from "lucide-react";
import { getActiveTenant } from "../lib/tenantApi";

/**
 * TenantShell — Wraps tenant-specific pages with a top brand bar
 * (logo, brand color, tenant name) and a side nav (Library, Career Paths, Workforce).
 */
export default function TenantShell({ active, children }) {
  const tenant = getActiveTenant();
  const { slug } = useParams();
  const nav = useNavigate();
  const brand = tenant?.brand_color || "#0F172A";
  const tslug = tenant?.slug || slug;

  const exit = () => {
    sessionStorage.removeItem("plena_tenant");
    nav(`/demo/${tslug}`, { replace: true });
  };

  const items = [
    { id: "library", label: "Yetkinlik Kütüphanesi", icon: BookOpen, path: `/tenant/${tslug}/library` },
    { id: "career-paths", label: "Kariyer Yolları", icon: GitBranch, path: `/tenant/${tslug}/career-paths` },
    { id: "workforce", label: "Çalışan Havuzu", icon: Users, path: `/tenant/${tslug}/workforce` },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {tenant?.logo_url ? (
              <img src={tenant.logo_url} alt={tenant?.name} className="h-8 max-w-[140px] object-contain" />
            ) : (
              <div className="flex items-center gap-2.5">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ background: brand }}
                >
                  {tenant?.name?.charAt(0) || "P"}
                </div>
                <div className="leading-tight">
                  <div className="font-display font-semibold text-sm text-slate-900">{tenant?.name || "Demo"}</div>
                  <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">Plena Growth · Sektörel Demo</div>
                </div>
              </div>
            )}
            <div
              className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.7rem] font-medium border"
              style={{ borderColor: `${brand}30`, color: brand, background: `${brand}08` }}
            >
              <Sparkles className="w-3 h-3" />
              {tenant?.industry === "retail" ? "Perakende Edisyonu" : "Sektörel Edisyon"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/personas"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 rounded-md hover:bg-slate-100"
              data-testid="back-to-personas"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Personalar
            </Link>
            <button
              onClick={exit}
              data-testid="tenant-exit-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50 rounded-md border border-rose-200"
            >
              <LogOut className="w-3.5 h-3.5" /> Çıkış
            </button>
          </div>
        </div>

        {/* Sub-nav */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto" data-testid="tenant-shell-nav">
            {items.map((it) => {
              const Icon = it.icon;
              const isActive = active === it.id;
              return (
                <Link
                  key={it.id}
                  to={it.path}
                  data-testid={`tenant-nav-${it.id}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-slate-900"
                      : "text-slate-500 border-transparent hover:text-slate-900 hover:bg-slate-50"
                  }`}
                  style={isActive ? { borderColor: brand, color: brand } : {}}
                >
                  <Icon className="w-4 h-4" />
                  {it.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">{children}</div>
    </div>
  );
}
